/* eslint-disable @nx/enforce-module-boundaries */
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { VANILLA_LAZY_ROUTES, VANILLA_SECONDARY_ROUTES } from '@frontend/loaders/routes';
import {
    IRetailConfig,
    IRouteData,
    LazyRouteGuard,
    LoggedInGuard,
    MaintenanceGuard,
    MaintenanceResolver,
    NotFoundPageGuard,
    RgyCheckGuard,
    RgyMatchGuard,
    SignUpRouteGuard,
} from '@frontend/oxygen/core';
import { rgyellow } from '@frontend/oxygen/core-bma';
import { routes as FavouritesRoutes } from '@frontend/oxygen/core-favourites';
import { BetPackAuthGuard, BetPackReviewAuthGuard, ProductSwitchResolver, ROOT_APP_ROUTES } from '@frontend/oxygen/core-lib';
import { routes as SbRoutes } from '@frontend/oxygen/core-sb';
import { routeData } from '@frontend/vanilla/core';

import { RootComponent } from '../../app.component';

export const appRoutes: Routes = [
    {
        path: '',
        component: RootComponent,
        data: {
            ...routeData({ allowAnonymous: true }),
            product: 'host',
            segment: 'main',
        },
        runGuardsAndResolvers: 'always',
        resolve: {
            __p: ProductSwitchResolver,
        },
        children: [
            {
                path: 'en',
                data: {
                    segment: 'vanilla',
                },
                children: [
                    ...VANILLA_LAZY_ROUTES,
                    ...ROOT_APP_ROUTES,
                    {
                        path: '**',
                        redirectTo: '/',
                    },
                ],
            },
            ...SbRoutes,
            ...FavouritesRoutes,
            {
                path: '',
                loadChildren: () => import('../coral-mobile-home/coral-mobile-home.module').then((m) => m.CoralMobileHomeModule),
                data: {
                    segment: 'home',
                    preload: true,
                },
            },
            {
                path: 'home',
                loadChildren: () => import('../coral-mobile-home/coral-mobile-home.module').then((m) => m.CoralMobileHomeModule),
                data: {
                    segment: 'home',
                    preload: true,
                },
            },
            {
                path: 'logout',
                loadChildren: () => import('../coral-mobile-home/coral-mobile-home.module').then((m) => m.CoralMobileHomeModule),
                data: {
                    segment: 'logout',
                    preload: true,
                },
            },
            {
                path: 'deposit',
                loadChildren: () => import('../coral-mobile-home/coral-mobile-home.module').then((m) => m.CoralMobileHomeModule),
                data: {
                    preload: true,
                },
            },
            {
                path: 'live-stream',
                loadChildren: () => import('@frontend/oxygen/core-bma').then((m) => m.LiveStreamWrapperModule),
                data: {
                    segment: 'liveStream',
                },
            },
            {
                path: 'az-sports',
                data: {
                    segment: 'azSports',
                },
                loadChildren: () => import('@frontend/oxygen/core-lazyatozsportpage').then((m) => m.LazyAToZSportPageModule),
            },
            {
                path: 'settings',
                loadChildren: () => import('@frontend/oxygen/core-bma').then((m) => m.UserSettingsModule),
                canActivate: [LoggedInGuard],
                runGuardsAndResolvers: 'always',
                data: {
                    segment: 'settings',
                },
            },
            {
                path: 'contact-us',
                loadChildren: () => import('@frontend/oxygen/core-contact-us').then((m) => m.ContactUsModule),
                data: {
                    preload: true,
                },
            },
            {
                path: 'static/:static-block',
                loadChildren: () => import('@frontend/oxygen/core-static').then((m) => m.StaticModule),
                data: {
                    preload: true,
                },
            },
            {
                path: 'freebets',
                loadChildren: () => import('@frontend/oxygen/core-freebets').then((m) => m.FreebetsModule),
                canActivate: [LoggedInGuard],
                data: {
                    segment: 'freebets',
                },
            },
            {
                path: '404',
                loadChildren: () => import('@frontend/oxygen/notfound').then((m) => m.NotFoundModule),
                data: {
                    segment: '404',
                },
            },
            {
                path: 'under-maintenance',
                loadComponent: () => import('@frontend/oxygen/core-shared').then((m) => m.MaintenanceComponent),
                resolve: { data: MaintenanceResolver },
                canDeactivate: [MaintenanceGuard],
            },
            // Modules lazy loaded by routes
            {
                path: 'tote-information',
                redirectTo: 'tote/information',
            },
            {
                path: 'tote',
                loadChildren: () => import('@frontend/oxygen/core-tote').then((m) => m.ToteModule),
            },
            {
                path: 'bet-finder',
                loadChildren: () => import('@frontend/oxygen/core-betfinder').then((m) => m.BetFinderModule),
            },
            {
                path: 'virtual-sports',
                loadChildren: () => import('@frontend/oxygen/core-vsbr').then((m) => m.VsbrModule),
            },
            {
                path: 'in-play',
                loadChildren: () => import('@frontend/oxygen/core-inplay').then((m) => m.InplayModule),
                data: {
                    preload: true,
                    loadAfterSeconds: 0,
                },
            },
            {
                path: 'byb-module',
                loadChildren: () => import('@frontend/oxygen/core-lazybybhistory').then((m) => m.LazyBybHistoryModule),
                canActivate: [LazyRouteGuard],
            },
            {
                path: 'betslip/add/:outcomeId',
                loadChildren: () => import('@frontend/oxygen/core-betslip').then((m) => m.BetslipModule),
            },
            {
                path: 'betslip/unavailable',
                loadChildren: () => import('@frontend/oxygen/core-betslip').then((m) => m.BetslipModule),
            },
            // Betslip module lazy loading
            {
                path: 'voucher-code',
                loadChildren: () => import('@frontend/oxygen/core-betslip').then((m) => m.BetslipModule),
            },
            {
                path: 'competitions/:sport/:className/:typeName',
                data: {
                    segment: 'competitionTypeEvents',
                },
                loadChildren: () => import('@frontend/oxygen/core-competitionssporttab').then((m) => m.CompetitionsTabModule),
            },
            {
                path: 'competition-filters',
                loadChildren: () => import('@frontend/oxygen/core-competitionfilters').then((m) => m.CompetitionFiltersModule),
            },
            // Odds Boost module lazy loading
            {
                path: 'superbooster',
                loadChildren: () => import('@frontend/oxygen/core-oddsboost').then((m) => m.OddsBoostModule),
            },
            {
                path: 'cashout',
                loadChildren: () => import('@frontend/oxygen/core-bethistory').then((m) => m.BetHistoryModule),
            },
            {
                path: 'open-bets',
                loadChildren: () => import('@frontend/oxygen/core-bethistory').then((m) => m.BetHistoryModule),
            },
            {
                path: 'bet-history',
                loadChildren: () => import('@frontend/oxygen/core-bethistory').then((m) => m.BetHistoryModule),
            },
            {
                path: 'in-shop-bets',
                loadChildren: () => import('@frontend/oxygen/core-bethistory').then((m) => m.BetHistoryModule),
            },
            // {
            //   path: 'lotto-results',
            //   redirectTo: 'lotto/results'
            // },
            {
                path: 'lotto',
                loadChildren: () => import('@frontend/oxygen/core-lotto').then((m) => m.LottoModule),
            },
            {
                path: 'big-competition',
                loadChildren: () => import('@frontend/oxygen/core-bigcompetitions').then((m) => m.BigCompetitionsModule),
            },

            // Retail module lazy loading
            {
                path: 'retail',
                /* below 2 lines added for grid decommision in sports book*/
                redirectTo: '/shop-locator',
                pathMatch: 'full',
            },
            {
                path: 'retail-upgrade',
                loadChildren: () => import('@frontend/oxygen/core-retail').then((m) => m.RetailModule),
                data: {
                    feature: 'upgrade',
                } as IRouteData<IRetailConfig>,
            },
            {
                path: 'bet-filter',
                loadChildren: () => import('@frontend/oxygen/core-retail').then((m) => m.RetailModule),
                data: {
                    feature: 'footballFilter',
                } as IRouteData<IRetailConfig>,
            },
            {
                path: 'bet-filter/:child',
                loadChildren: () => import('@frontend/oxygen/core-retail').then((m) => m.RetailModule),
                data: {
                    feature: 'footballFilter',
                } as IRouteData<IRetailConfig>,
            },
            {
                path: 'bet-filter/:child/:child',
                loadChildren: () => import('@frontend/oxygen/core-retail').then((m) => m.RetailModule),
                data: {
                    feature: 'footballFilter',
                } as IRouteData<IRetailConfig>,
            },
            {
                path: 'bet-tracker',
                loadChildren: () => import('@frontend/oxygen/core-retail').then((m) => m.RetailModule),
                data: {
                    feature: 'shopBetTracker',
                } as IRouteData<IRetailConfig>,
            },
            {
                path: 'betbundle-market',
                canActivate: [BetPackAuthGuard, RgyCheckGuard],
                data: { moduleName: rgyellow.BET_BUNDLES },
                loadChildren: () => import('@frontend/oxygen/core-betpackmarket').then((m) => m.BetpackMarketModule),
            },
            {
                path: 'betbundle-review',
                canActivate: [BetPackReviewAuthGuard],
                loadChildren: () => import('@frontend/oxygen/core-betpackreview').then((m) => m.BetpackReviewModule),
            },
            {
                path: 'shop-locator',
                loadChildren: () => import('@frontend/oxygen/core-retail').then((m) => m.RetailModule),
                data: {
                    feature: 'shopLocator',
                } as IRouteData<IRetailConfig>,
            },
            // Racing lazy loading
            {
                path: 'horse-racing',
                loadChildren: () => import('@frontend/oxygen/core-racing').then((m) => m.HorseracingModule),
            },
            {
                path: 'greyhound-racing',
                loadChildren: () => import('@frontend/oxygen/core-racing').then((m) => m.GreyhoundModule),
            },
            // EDP
            {
                path: 'edp-lazy-load',
                loadChildren: () => import('@frontend/oxygen/core-edp').then((m) => m.EdpModule),
                canActivate: [LazyRouteGuard],
                data: {
                    preload: true,
                    loadAfterSeconds: 0,
                },
            },
            // Banners Module
            {
                path: 'banners-section',
                loadChildren: () => import('@frontend/oxygen/core-banners').then((m) => m.BannersModule),
                canActivate: [LazyRouteGuard],
            },
            {
                path: 'market-description-lazy-load',
                loadChildren: () => import('@frontend/oxygen/core-market-description').then((m) => m.MarketDescriptionModule),
                canActivate: [LazyRouteGuard],
            },

            {
                path: 'odds-card-highlight-carousel-lazy-load',
                loadChildren: () => import('@frontend/oxygen/core-oddscardhighlight').then((m) => m.OddsCardHighlightCarouselModule),
                canActivate: [LazyRouteGuard],
                data: {
                    preload: true,
                },
            },
            // Sport (SB) lazy loading
            {
                path: 'sport',
                loadChildren: () => import('@frontend/oxygen/core-lazysportmodule').then((m) => m.LazySportModule),
            },

            // Event (SB) lazy loading
            {
                path: 'event',
                loadChildren: () => import('@frontend/oxygen/core-lazyeventmodule').then((m) => m.LazyEventModule),
            },

            // Coupons lazy loading
            {
                path: 'coupons',
                loadChildren: () => import('@frontend/oxygen/core-lazycouponspage').then((m) => m.LazyCouponsPageModule),
            },

            // Olympics lazy loading
            {
                path: 'olympics',
                loadChildren: () => import('@frontend/oxygen/core-olympics').then((m) => m.OlympicsModule),
            },
            {
                path: 'forecast-tricast-lazy-load',
                loadChildren: () => import('@frontend/oxygen/core-forecasttricast').then((m) => m.ForecastTricastModule),
                canActivate: [LazyRouteGuard],
            },
            {
                path: 'event-video-stream-lazy-load',
                loadChildren: () => import('@frontend/oxygen/core-eventvideostream').then((m) => m.LazyEventVideoStreamModule),
                canActivate: [LazyRouteGuard],
            },
            // Bet radar lazy loading
            {
                path: 'bet-radar-lazy-load',
                loadChildren: () => import('@frontend/oxygen/core-betradarprovider').then((m) => m.BetRadarCoralModule),
                canActivate: [LazyRouteGuard],
            },
            // Receipt header lazy loading
            {
                path: 'receipt-header-lazy-load',
                loadChildren: () => import('@frontend/oxygen/core-receiptheader').then((m) => m.ReceiptHeaderModule),
                canActivate: [LazyRouteGuard],
            },
            // bet receipt bpmp tokens lazy load
            {
                path: 'bet-receipt-bpmp-tokens-lazy-load',
                loadChildren: () => import('@frontend/oxygen/core-bpmpfreebettokens').then((m) => m.BpmpFreeBetTokensModule),
                canActivate: [LazyRouteGuard],
            },
            // bet pack onboarding
            {
                path: 'bet-pack-onboarding-lazy-load',
                loadChildren: () => import('@frontend/oxygen/core-betpackonboarding').then((m) => m.BetpackOnBoardingModule),
                canActivate: [LazyRouteGuard],
            },
            // Signposting lazy loading
            {
                path: 'signposting-lazy-load',
                loadChildren: () => import('@frontend/oxygen/core-signposting').then((m) => m.SignpostingModule),
                canActivate: [LazyRouteGuard],
            },
            // Racing-Post-Verdict lazy loading
            {
                path: 'racing-post-verdict-lazy-load',
                loadChildren: () => import('@frontend/oxygen/core-racingpostverdict').then((m) => m.RacingPostVerdictModule),
                canActivate: [LazyRouteGuard],
            },
            // Racing Post lazy loading
            {
                path: 'racing-post-lazy-load',
                loadChildren: () => import('@frontend/oxygen/core-racingposttip').then((m) => m.RacingPostTipModule),
                canActivate: [LazyRouteGuard],
            },
            // Next Race for Betslip lazy loading
            {
                path: 'next-races-lazy-load',
                loadChildren: () => import('@frontend/oxygen/core-nextracestobetslip').then((m) => m.NextRacesToBetslipModule),
                canActivate: [LazyRouteGuard],
            },
            // Runner-spotlight lazy loading
            {
                path: 'runner-spotlight-lazy-load',
                loadChildren: () => import('@frontend/oxygen/core-runnerspotlight').then((m) => m.RunnerSpotlightModule),
                canActivate: [LazyRouteGuard],
            },
            {
                path: 'market-selector',
                loadChildren: () => import('@frontend/oxygen/core-marketselector').then((m) => m.MarketSelectorModule),
                canActivate: [LazyRouteGuard],
            },
            // Quickbet
            {
                path: 'quickbet-lazy-load',
                loadChildren: () => import('@frontend/oxygen/core-quickbet').then((m) => m.QuickbetModule),
                canActivate: [LazyRouteGuard],
            },
            // Quick Deposit
            {
                path: 'quick-deposit-lazy-load',
                loadChildren: () => import('@frontend/oxygen/core-quickdeposit').then((m) => m.QuickDepositModule),
                canActivate: [LazyRouteGuard],
            },
            {
                path: 'promotions',
                loadChildren: () => import('@frontend/oxygen/core-promotions').then((m) => m.PromotionsModule),
                data: {
                    path: 'promotions/retail',
                    feature: 'promotions',
                    moduleName: rgyellow.PROMOTIONS,
                },
                canMatch: [RgyMatchGuard],
                canActivate: [RgyCheckGuard],
            },

            {
                // path: 'euro',
                path: 'matchday-rewards',
                loadChildren: () => import('@frontend/oxygen/core-euro').then((m) => m.EuroModule),
            },
            {
                path: 'signup',
                canActivate: [SignUpRouteGuard],
                children: [],
            },
            // Correct4
            {
                path: 'footballsuperseries',
                loadChildren: () => import('@frontend/oxygen/core-questionengine').then((m) => m.QuestionEngineModule),
                data: {
                    segment: 'question-engine',
                    moduleName: rgyellow.FOOTBALL_SUPER_SERIES,
                },
                canMatch: [RgyMatchGuard],
            },
            // Other Question Engine versions
            {
                path: 'qe/:sourceId',
                loadChildren: () => import('@frontend/oxygen/core-questionengine').then((m) => m.QuestionEngineModule),
                data: {
                    segment: 'question-engine',
                },
            },
            {
                path: 'seoStaticBlock-lazy-load',
                loadChildren: () => import('@frontend/oxygen/core-seostaticblock').then((m) => m.SeoStaticBlockModule),
                canActivate: [LazyRouteGuard],
            },
            // MaxPayoutError Component
            {
                path: 'maxpayout-error-container',
                loadChildren: () => import('@frontend/oxygen/core-maxpayouterrorcontainer').then((m) => m.MaxpayoutErrorContainerModule),
                canActivate: [LazyRouteGuard],
            },
            {
                path: 'extra-place-signposting',
                loadChildren: () => import('@frontend/oxygen/core-extraplacesignposting').then((m) => m.ExtraPlaceSignpostingModule),
                canActivate: [LazyRouteGuard],
            },
            {
                path: 'module-ribbon-lazy-load',
                loadChildren: () => import('@frontend/oxygen/core-moduleribbon').then((m) => m.RibbonModule),
                canActivate: [LazyRouteGuard],
            },
            {
                path: 'my-stable',
                loadChildren: () => import('@frontend/oxygen/core-mystablemodule-root').then((m) => m.MyStableModule),
                canActivate: [LoggedInGuard],
            },
            {
                path: '**',
                loadChildren: () => import('@frontend/oxygen/notfound').then((m) => m.NotFoundModule),
                canActivate: [NotFoundPageGuard],
            },
        ],
    },
    ...VANILLA_SECONDARY_ROUTES,
];

@NgModule({
    imports: [RouterModule.forChild(appRoutes)],
    exports: [RouterModule],
})
export class RootRoutingModule {}
